import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function NotificationSettings({ settings, setSettings }) {
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const handleTestTelegram = async () => {
    if (!settings.telegram_bot_token) {
      toast.error('Сначала введите Bot Token и сохраните настройки');
      return;
    }

    setTesting(true);
    try {
      await axios.post(`${API}/test-telegram-notification`, {}, getAuthHeaders());
      toast.success('Тестовое уведомление отправлено! Проверьте Telegram.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отправки уведомления');
    } finally {
      setTesting(false);
    }
  };

  const handleCheckExpiring = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/check-expiring-parameters`, {}, getAuthHeaders());
      const count = response.data.notifications_sent;
      if (count > 0) {
        toast.success(`Отправлено ${count} уведомлений об истекающих сроках`);
      } else {
        toast.info('Нет параметров, требующих уведомления');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка проверки сроков');
    } finally {
      setTesting(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Введите email для тестирования');
      return;
    }

    setTesting(true);
    try {
      await axios.post(`${API}/email/test?email=${encodeURIComponent(testEmail)}`, {}, getAuthHeaders());
      toast.success(`Тестовое письмо отправлено на ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отправки email. Проверьте SMTP настройки.');
    } finally {
      setTesting(false);
    }
  };

  const handleSendReminders = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/email/send-reminders`, {}, getAuthHeaders());
      toast.success('Напоминания о рейсах отправлены');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отправки напоминаний');
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      {/* Telegram */}
      <Card className="glass-effect shadow-2xl border-2 border-blue-200 hover:border-blue-400 transition-all">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-100">
          <CardTitle className="flex items-center text-2xl">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
              <Bell className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            Telegram
          </CardTitle>
          <CardDescription className="text-base font-medium text-gray-600">
            Настройте отправку уведомлений в Telegram
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-2">
            <Label htmlFor="telegram_bot_token" className="text-base font-bold text-gray-700">Bot Token</Label>
            <Input
              id="telegram_bot_token"
              data-testid="telegram-token-input"
              type="password"
              placeholder="Введите Telegram Bot Token"
              value={settings.telegram_bot_token || ''}
              onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
              className="border-2 border-blue-200 focus:border-blue-500 text-base"
            />
            <p className="text-sm text-gray-500 font-medium">
              💡 Получите токен у @BotFather в Telegram
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="telegram_chat_id" className="text-base font-bold text-gray-700">Chat ID / Username</Label>
            <Input
              id="telegram_chat_id"
              data-testid="telegram-chat-input"
              type="password"
              placeholder="@username или chat_id"
              value={settings.telegram_chat_id}
              onChange={(e) => setSettings({ ...settings, telegram_chat_id: e.target.value })}
              className="border-2 border-blue-200 focus:border-blue-500 text-base"
            />
          </div>
          
          <div className="flex flex-wrap gap-3 pt-6 border-t-2 border-blue-100">
            <Button
              onClick={handleTestTelegram}
              disabled={testing || !settings.telegram_bot_token}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              data-testid="test-telegram-button"
            >
              {testing ? '⏳ Отправка...' : '📤 Отправить тест'}
            </Button>
            <Button
              onClick={handleCheckExpiring}
              disabled={testing || !settings.telegram_bot_token}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              data-testid="check-expiring-button"
            >
              {testing ? '⏳ Проверка...' : '🔍 Проверить истекающие сроки'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp */}
      <Card className="glass-effect shadow-2xl border-2 border-green-200 hover:border-green-400 transition-all">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center text-2xl">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                  <Bell className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                WhatsApp
              </CardTitle>
              <CardDescription className="text-base font-medium text-gray-600">
                Настройте отправку уведомлений в WhatsApp
              </CardDescription>
            </div>
            <Switch
              data-testid="whatsapp-switch"
              checked={settings.whatsapp_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, whatsapp_enabled: checked })}
              className="data-[state=checked]:bg-green-500"
            />
          </div>
        </CardHeader>
        {settings.whatsapp_enabled && (
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label htmlFor="whatsapp_number" className="text-base font-bold text-gray-700">Номер телефона</Label>
              <Input
                id="whatsapp_number"
                data-testid="whatsapp-number-input"
                type="text"
                placeholder="+380XXXXXXXXX"
                value={settings.whatsapp_number || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                className="border-2 border-green-200 focus:border-green-500 text-base"
              />
              <p className="text-sm text-gray-500 font-medium">
                💡 Требуется интеграция WhatsApp Business API или Twilio
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Viber */}
      <Card className="glass-effect shadow-2xl border-2 border-purple-200 hover:border-purple-400 transition-all">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center text-2xl">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                  <Bell className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                Viber
              </CardTitle>
              <CardDescription className="text-base font-medium text-gray-600">
                Настройте отправку уведомлений в Viber
              </CardDescription>
            </div>
            <Switch
              data-testid="viber-switch"
              checked={settings.viber_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, viber_enabled: checked })}
              className="data-[state=checked]:bg-purple-500"
            />
          </div>
        </CardHeader>
        {settings.viber_enabled && (
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label htmlFor="viber_token" className="text-base font-bold text-gray-700">Viber Bot Token</Label>
              <Input
                id="viber_token"
                data-testid="viber-token-input"
                type="text"
                placeholder="Введите Viber Bot Token"
                value={settings.viber_token || ''}
                onChange={(e) => setSettings({ ...settings, viber_token: e.target.value })}
                className="border-2 border-purple-200 focus:border-purple-500 text-base"
              />
              <p className="text-sm text-gray-500 font-medium">
                💡 Получите токен в Viber Admin Panel
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Email */}
      <Card className="glass-effect shadow-2xl border-2 border-red-200 hover:border-red-400 transition-all">
        <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b-2 border-red-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center text-2xl">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                  <Bell className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                Email
              </CardTitle>
              <CardDescription className="text-base font-medium text-gray-600">
                Уведомления клиентам о рейсах
              </CardDescription>
            </div>
            <Switch
              data-testid="email-switch"
              checked={settings.email_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
              className="data-[state=checked]:bg-red-500"
            />
          </div>
        </CardHeader>
        {settings.email_enabled && (
          <CardContent className="space-y-4 pt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-blue-800 mb-2">📧 Автоматические письма:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>10:00</strong> — Напоминание клиенту за 1 день до рейса</li>
                <li>• <strong>18:00</strong> — Запрос отзыва после завершения рейса</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                💡 Письма отправляются на email указанный в рейсе (поле "Email клиента")
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_address" className="text-base font-bold text-gray-700">Email отправителя</Label>
              <Input
                id="email_address"
                data-testid="email-address-input"
                type="email"
                placeholder="noreply@bigbus.lt"
                value={settings.email_address || ''}
                onChange={(e) => setSettings({ ...settings, email_address: e.target.value })}
                className="border-2 border-red-200 focus:border-red-500 text-base"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_server" className="text-base font-bold text-gray-700">SMTP сервер</Label>
                <Input
                  id="smtp_server"
                  data-testid="smtp-server-input"
                  type="text"
                  placeholder="smtp.gmail.com"
                  value={settings.smtp_server || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_server: e.target.value })}
                  className="border-2 border-red-200 focus:border-red-500 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port" className="text-base font-bold text-gray-700">SMTP порт</Label>
                <Input
                  id="smtp_port"
                  data-testid="smtp-port-input"
                  type="number"
                  placeholder="587"
                  value={settings.smtp_port || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                  className="border-2 border-red-200 focus:border-red-500 text-base"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_username" className="text-base font-bold text-gray-700">SMTP логин</Label>
              <Input
                id="smtp_username"
                data-testid="smtp-username-input"
                type="text"
                placeholder="your@email.com"
                value={settings.smtp_username || ''}
                onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                className="border-2 border-red-200 focus:border-red-500 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_password" className="text-base font-bold text-gray-700">SMTP пароль</Label>
              <Input
                id="smtp_password"
                data-testid="smtp-password-input"
                type="password"
                placeholder="••••••••"
                value={settings.smtp_password || ''}
                onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                className="border-2 border-red-200 focus:border-red-500 text-base"
              />
              <p className="text-sm text-gray-500">
                💡 Для Gmail используйте <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">App Password</a>
              </p>
            </div>

            <div className="border-t-2 border-red-100 pt-4 mt-4">
              <Label className="text-base font-bold text-gray-700 mb-2 block">Тестирование</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="test@email.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="border-2 border-red-200 focus:border-red-500"
                />
                <Button
                  onClick={handleTestEmail}
                  disabled={testing || !settings.email_enabled}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold whitespace-nowrap"
                >
                  {testing ? '⏳...' : '📤 Тест'}
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSendReminders}
                disabled={testing}
                variant="outline"
                className="flex-1 border-2 border-red-300 text-red-600 hover:bg-red-50"
              >
                📅 Отправить напоминания
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </>
  );
}
